/**
 * Idiomas da interface: português (Brasil, idioma de origem), espanhol e inglês.
 *
 * - O texto em português é a própria chave (estilo gettext): `t("Salvar")`, `t("Olá, {nome}!", { nome })`.
 *   Os catálogos `es.json` e `en.json` mapeiam o texto em português para a tradução. Texto sem tradução aparece em
 *   português (nunca quebra a tela) e o teste `tests/infra/test_i18n.py` acusa a falta.
 * - Plural: `tn("{n} treino", "{n} treinos", n)` — no catálogo, a chave é "{n} treino|{n} treinos" e o valor,
 *   [singular, plural].
 * - O idioma vale para a página inteira: trocar recarrega o app (os rótulos montados no carregamento dos módulos
 *   também mudam). Ordem de escolha: preferência da conta (servidor) → escolha salva neste navegador → idioma do
 *   navegador/aparelho → português.
 * - Frases com elementos (negrito, links): `tj("Escreva para {to}.", { to: <strong>{to}</strong> })` — a frase
 *   continua inteira no catálogo e o tradutor pode mudar a ordem das partes.
 * - Os textos passados a t()/tn()/tj()/tnj() precisam ser literais (o extrator não lê variáveis).
 */
import { Fragment, createElement, type ReactNode } from "react";
import en from "./en.json";
import es from "./es.json";

export type Locale = "pt-BR" | "es" | "en";
export const LOCALES: { code: Locale; label: string; short: string }[] = [
  { code: "pt-BR", label: "Português", short: "PT" },
  { code: "es", label: "Español", short: "ES" },
  { code: "en", label: "English", short: "EN" },
];
const STORAGE_KEY = "eapace.locale";
type Catalog = Record<string, string | string[]>;
const CATALOGS: Record<Locale, Catalog> = { "pt-BR": {}, es: es as Catalog, en: en as Catalog };

export function normalizeLocale(tag: string | null | undefined): Locale | null {
  const t = (tag ?? "").toLowerCase();
  if (t.startsWith("pt")) return "pt-BR";
  if (t.startsWith("es")) return "es";
  if (t.startsWith("en")) return "en";
  return null;
}

function detect(): Locale {
  try {
    const saved = normalizeLocale(localStorage.getItem(STORAGE_KEY));
    if (saved) return saved;
  } catch {
    /* armazenamento indisponível */
  }
  const langs = typeof navigator !== "undefined" ? [...(navigator.languages ?? []), navigator.language] : [];
  for (const l of langs) {
    const n = normalizeLocale(l);
    if (n) return n;
  }
  return "pt-BR";
}

/** Idioma em uso nesta página (fixo até recarregar). */
export const LOCALE: Locale = detect();
const catalog = CATALOGS[LOCALE];
const plural = new Intl.PluralRules(LOCALE);
if (typeof document !== "undefined") document.documentElement.lang = LOCALE;

type Params = Record<string, string | number | null | undefined>;

function fill(text: string, params?: Params): string {
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (m, k: string) => (k in params && params[k] !== undefined && params[k] !== null ? String(params[k]) : m));
}

/** Traduz um texto em português (literal). */
export function t(pt: string, params?: Params): string {
  const tr = catalog[pt];
  return fill(typeof tr === "string" && tr ? tr : pt, params);
}

/** Plural: singular e plural em português; `{n}` recebe a quantidade. */
export function tn(one: string, other: string, n: number, params?: Params): string {
  const tr = catalog[`${one}|${other}`];
  const forms = Array.isArray(tr) && tr.length === 2 ? tr : [one, other];
  const form = plural.select(n) === "one" ? forms[0] : forms[1];
  return fill(form, { n: n.toLocaleString(LOCALE), ...params });
}

type NodeParams = Record<string, ReactNode>;

function fillNodes(text: string, params: NodeParams): ReactNode {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(/\{(\w+)\}/g)) {
    const k = m[1];
    if (!(k in params)) continue;
    if (m.index! > last) out.push(text.slice(last, m.index));
    const v = params[k];
    out.push(typeof v === "number" ? v.toLocaleString(LOCALE) : createElement(Fragment, { key: `p${i++}` }, v));
    last = m.index! + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return createElement(Fragment, null, ...out);
}

/** Como t(), mas os parâmetros podem ser elementos React (negrito, links, datas formatadas). */
export function tj(pt: string, params: NodeParams): ReactNode {
  const tr = catalog[pt];
  return fillNodes(typeof tr === "string" && tr ? tr : pt, params);
}

/** Como tn(), com parâmetros que podem ser elementos React. */
export function tnj(one: string, other: string, n: number, params: NodeParams): ReactNode {
  const tr = catalog[`${one}|${other}`];
  const forms = Array.isArray(tr) && tr.length === 2 ? tr : [one, other];
  return fillNodes(plural.select(n) === "one" ? forms[0] : forms[1], { n: n.toLocaleString(LOCALE), ...params });
}

/** Grava a escolha neste navegador e recarrega o app no novo idioma. Quem chama salva antes na conta, se houver. */
export function applyLocale(code: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    /* sem armazenamento: vale só até recarregar */
  }
  if (code !== LOCALE) window.location.reload();
}

const PENDING_KEY = "eapace.locale.pending";

/** A escolha feita sem login (ou sem rede) deve virar a preferência da conta no próximo login. */
export function markPendingLocale(): void {
  try {
    localStorage.setItem(PENDING_KEY, "1");
  } catch {
    /* ignore */
  }
}

/** Há uma escolha deste navegador esperando para ser salva na conta? (e limpa a marca) */
export function takePendingLocale(): boolean {
  try {
    const v = localStorage.getItem(PENDING_KEY) === "1";
    localStorage.removeItem(PENDING_KEY);
    return v;
  } catch {
    return false;
  }
}

/** Guarda a preferência que veio da conta (servidor). Retorna true se a página precisa recarregar. */
export function adoptAccountLocale(code: string | null | undefined): boolean {
  const n = normalizeLocale(code);
  if (!n) return false;
  try {
    localStorage.setItem(STORAGE_KEY, n);
  } catch {
    /* ignore */
  }
  return n !== LOCALE;
}
